import * as React from 'react';
import { skeletonSource, skeletonStyle, unionSize, SKELETON_DELAY_MS, type MeasuredSize, type SkeletonSize } from './skeleton-box.ts';
import './skeleton.css';

// Stands in for content while it loads, taking up the size the content will
// have, so nothing jumps when it arrives. Presentational: the caller says
// whether it is loading. The size comes from, in order: `shape` (the content
// drawn invisibly, e.g. the card with sample data: exact), the size the
// content had the last time it was shown (a reload), `width`/`height`,
// `lines`, else one line. Loading shorter than `delay` shows only the
// reserved space, so quick loads do not flash.
//
//   <Skeleton loading={!data} shape={<Card data={SAMPLE} />}>{data && <Card data={data} />}</Skeleton>
//   <Skeleton loading={!count} width="6rem" height="1.5rem">{count} games</Skeleton>
//   <Skeleton loading={!text} lines={3}>{text}</Skeleton>
//   <Skeleton loading={!data} variant="circular" width="6rem" height="6rem" />
//
// As Material UI's Skeleton: `variant` sets the corners (text, rectangular,
// rounded — the default — or circular) and `animation` the motion (a wave
// sweeping across, the default; a pulse; or none).

/** The skeleton's corners, as Material UI's variants. */
export type SkeletonVariant = 'text' | 'rectangular' | 'rounded' | 'circular';
/** The skeleton's motion once shown: a band sweeping across, a pulse, or none. */
export type SkeletonAnimation = 'wave' | 'pulse' | false;

const VARIANT_RADIUS: Record<SkeletonVariant, string | undefined> = {
  text: 'var(--radius-sm, 0.25rem)',
  rectangular: '0',
  rounded: undefined, // the theme's medium radius, the default
  circular: '50%',
};

export interface SkeletonProps extends SkeletonSize {
  /** Whether the content is still loading. */
  loading: boolean;
  /** The content, rendered as is once loaded. While loading, it is drawn invisibly to size the skeleton when no `shape` is given. */
  children?: React.ReactNode;
  /** What the content will look like (the same component with sample data), drawn invisibly while loading to give the skeleton its exact size. */
  shape?: React.ReactNode;
  /** Milliseconds of loading before the skeleton shows; the space is reserved from the start. Default 250. */
  delay?: number;
  /** What a screen reader announces while loading. Default "Loading". */
  label?: string;
  /** Inline content (a number in a sentence) rather than a block. */
  inline?: boolean;
  /** The skeleton's corner radius, a CSS length; overrides `variant`. Default the theme's medium radius (a line's own for `lines`). */
  radius?: string;
  /** The corners: text, rectangular, rounded (default) or circular (give equal `width` and `height`). */
  variant?: SkeletonVariant;
  /** The motion once shown: 'wave' (default), 'pulse', or false for none. */
  animation?: SkeletonAnimation;
  className?: string;
}

/** Whether a node renders anything: false, null, undefined and '' do not. */
function renders(node: React.ReactNode): boolean {
  if (node === null || node === undefined || node === false || node === true || node === '') return false;
  if (Array.isArray(node)) return node.some(renders);
  return true;
}

export const Skeleton: React.FC<SkeletonProps> = ({ loading, children, shape, delay = SKELETON_DELAY_MS, label = 'Loading', inline = false, radius: given, variant = 'rounded', animation = 'wave', className, width, height, lines }) => {
  const radius = given ?? VARIANT_RADIUS[variant];
  const Tag = inline ? 'span' : 'div';
  const content = React.useRef<HTMLElement>(null);
  const last = React.useRef<MeasuredSize | undefined>(undefined);
  const [shown, setShown] = React.useState(loading && delay <= 0);

  // While the content shows, keep its size, for the next time it loads.
  React.useLayoutEffect(() => {
    const wrapper = content.current;
    if (loading || !wrapper) return;
    const measure = () => {
      const size = unionSize(Array.from(wrapper.children, (c) => c.getBoundingClientRect()));
      if (size) last.current = size;
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    for (const child of Array.from(wrapper.children)) observer.observe(child);
    return () => observer.disconnect();
  }, [loading, children]);

  React.useEffect(() => {
    if (!loading || delay <= 0) {
      setShown(loading);
      return;
    }
    setShown(false);
    const timer = setTimeout(() => setShown(true), delay);
    return () => clearTimeout(timer);
  }, [loading, delay]);

  if (!loading) {
    // `display: contents`: the wrapper adds no box, so the content lays out
    // exactly as without it; it is only there to measure the content.
    return (
      <Tag ref={content as never} className="skeleton-content">
        {children}
      </Tag>
    );
  }

  const ghost = shape ?? (renders(children) ? children : undefined);
  const size = { width, height, lines };
  const source = skeletonSource(ghost !== undefined, size, last.current);
  // How the skeleton lays out:
  // - a block shape: the wrapper adds no box (`display: contents`) and the
  //   shape's own top-level elements take the tint, so they lay out exactly
  //   as the content will, in any grid or flex container;
  // - an inline shape: an inline box around the invisible text, the text's own size;
  // - lines: a bar per line;
  // - else a box of the measured, given or default size.
  const mode = source === 'shape' ? (inline ? 'skeleton-inline' : 'skeleton-shape') : source === 'lines' ? 'skeleton-lines' : 'skeleton-box';
  const motion = animation === 'pulse' ? 'skeleton-pulse' : animation === false ? 'skeleton-still' : '';
  const classes = ['skeleton', mode, inline && mode === 'skeleton-box' ? 'skeleton-box-inline' : '', motion, shown ? 'skeleton-shown' : '', className ?? '']
    .filter(Boolean)
    .join(' ');
  const style = { ...skeletonStyle(source, size, last.current, inline), ...(radius ? { '--skeleton-radius': radius } : {}) } as React.CSSProperties;
  return (
    <Tag className={classes} style={style} role="status" aria-busy="true" aria-label={label}>
      {source === 'shape' && (
        <Tag className="skeleton-ghost" aria-hidden="true">
          {ghost}
        </Tag>
      )}
      {source === 'lines' &&
        Array.from({ length: lines! }, (_, i) => <span key={i} className="skeleton-line" aria-hidden="true" />)}
    </Tag>
  );
};
